import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { requestBuyout, requestCancellation } from "@/lib/api";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-white placeholder:text-fog/40 outline-none transition-colors duration-300 focus:border-white/60";
const money = (cents) => `$${(cents / 100).toLocaleString()}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;

function RequestCard({ title, copy, button, onSubmit, result, error, testId }) {
  const [email, setEmail] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onSubmit({ email, order_reference: reference });
    setBusy(false);
  };
  return (
    <div className="border border-white/15 p-6 md:p-8" data-testid={`${testId}-card`}>
      <p className="font-display text-2xl font-medium tracking-[-0.02em]">{title}</p>
      <p className="mt-3 text-sm leading-relaxed text-fog">{copy}</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input type="email" required placeholder="Email used at purchase" aria-label="Email"
          data-testid={`${testId}-email`} className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
        <input required placeholder="Order reference (e.g. FS-1A2B3C)" aria-label="Order reference"
          data-testid={`${testId}-reference`} className={inputCls} value={reference}
          onChange={(e) => setReference(e.target.value)} />
        <button type="submit" disabled={busy} data-testid={`${testId}-submit`}
          className="group inline-flex items-center gap-2 border border-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 ease-cinematic hover:bg-white hover:text-obsidian disabled:opacity-50">
          {busy ? "Sending…" : button}
          <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5" />
        </button>
      </form>
      {error && <p className="mt-4 text-sm text-red-400" data-testid={`${testId}-error`}>{error}</p>}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
          className="mt-6 border border-white/15 p-5" data-testid={`${testId}-result`}>
          <p className="text-sm leading-relaxed text-white">{result.message}</p>
          {result.next_billing_date && (
            <div className="mt-4 space-y-2 border-t border-white/10 pt-4 text-xs text-fog">
              <p>Next billing date: {fmtDate(result.next_billing_date)}</p>
              {result.effective_cancellation_date && (
                <p>Cancellation effective: {fmtDate(result.effective_cancellation_date)}</p>
              )}
              <p className="text-white/80">
                {result.already_scheduled
                  ? "Your subscription is already scheduled to end."
                  : result.notice_met
                    ? "Your request meets the 30-day notice period."
                    : "Your next billing date is inside the 30-day notice window — that payment will still be charged."}
              </p>
            </div>
          )}
          {result.buyout_price_cents != null && (
            <p className="mt-4 border-t border-white/10 pt-4 text-xs text-fog">
              Buyout price: <span className="text-white">{money(result.buyout_price_cents)}</span> — the current Own It price.
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default function ManagePurchase() {
  const [cancelResult, setCancelResult] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const [buyoutResult, setBuyoutResult] = useState(null);
  const [buyoutError, setBuyoutError] = useState("");

  const submitCancel = async (payload) => {
    setCancelError("");
    setCancelResult(null);
    try {
      const { data } = await requestCancellation(payload);
      setCancelResult(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setCancelError(typeof d === "string" ? d : "Something went wrong — try again.");
    }
  };

  const submitBuyout = async (payload) => {
    setBuyoutError("");
    setBuyoutResult(null);
    try {
      const { data } = await requestBuyout(payload);
      setBuyoutResult(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setBuyoutError(typeof d === "string" ? d : "Something went wrong — try again.");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Seo title="Manage Your Purchase" noindex path="/manage" />
      <div className="mx-auto max-w-4xl px-5 pb-32 pt-32 md:px-10 md:pt-44">
        <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">Manage your purchase</p>
        <h1 className="mt-4 font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] md:text-6xl">
          Your plan, your call.
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-fog md:text-base">
          Enter the email you used at purchase and your order reference — both are in your confirmation email.
        </p>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          <RequestCard
            testId="cancel"
            title="Cancel a monthly plan"
            copy="No minimum term. Cancellation takes effect at your next billing date when requested at least 30 days ahead; inside that window, the upcoming payment still charges and cancellation follows it. Your website stays online while a request is processed."
            button="Request cancellation"
            onSubmit={submitCancel}
            result={cancelResult}
            error={cancelError}
          />
          <RequestCard
            testId="buyout"
            title="Buy out your website"
            copy="Own your build outright at the current Own It price. Monthly payments already made don't count toward it. We handle the purchase and transition directly with you — your site stays online throughout."
            button="Request buyout"
            onSubmit={submitBuyout}
            result={buyoutResult}
            error={buyoutError}
          />
        </div>
      </div>
    </div>
  );
}
